<template>
  <el-dialog
    v-model="visible"
    title="切换频道"
    width="520px"
    :close-on-click-modal="false"
    class="channel-dialog"
    @close="handleClose"
  >
    <div class="dialog-hero">
      <div class="hero-icon-wrap">
        <el-icon :size="22" color="#fff"><Switch /></el-icon>
      </div>
      <div class="hero-text">
        <h3>切换终端频道</h3>
        <p>切换设备接入链路标识，可用于重置安全凭证</p>
      </div>
    </div>

    <!-- 当前信息 -->
    <div class="current-info">
      <div class="info-label">终端编号</div>
      <div class="info-value">{{ deviceId || "(无)" }}</div>
      <div class="info-label" style="margin-top: 12px">当前频道</div>
      <div class="current-channel-badge">频道 {{ currentChannel }}</div>
    </div>

    <!-- 频道选择 -->
    <div class="channel-section">
      <div class="section-title">选择新频道</div>
      <div class="channel-options">
        <div
          v-for="ch in [1, 2, 3]"
          :key="ch"
          class="channel-option"
          :class="{
            active: selectedChannel === ch,
            current: ch === currentChannel,
          }"
          @click="selectedChannel = ch"
        >
          <div class="channel-radio">
            <div v-if="selectedChannel === ch" class="radio-dot"></div>
          </div>
          <div class="channel-info">
            <div class="channel-name">频道 {{ ch }}</div>
            <div class="channel-desc">
              <template v-if="ch === 1">默认接入组</template>
              <template v-else-if="ch === 2">备用接入组 A</template>
              <template v-else>备用接入组 B</template>
            </div>
          </div>
          <div v-if="ch === currentChannel" class="current-tag">当前</div>
        </div>
      </div>
    </div>

    <el-alert type="warning" :closable="false" class="warn-alert">
      <template #title>
        <div class="warn-text">
          · 切换后旧频道将被拉黑，终端会被踢线<br />
          · 终端固件需循环尝试三个频道找到新活跃频道<br />
          · endpointId 和绑船关系保持不变
        </div>
      </template>
    </el-alert>

    <template #footer>
      <el-button size="large" @click="visible = false">取消</el-button>
      <el-button
        type="primary"
        size="large"
        :loading="submitting"
        :disabled="selectedChannel === currentChannel"
        class="submit-btn"
        @click="handleSubmit"
      >
        <el-icon><Check /></el-icon>
        <span>确认切换到频道 {{ selectedChannel }}</span>
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Switch, Check } from "@element-plus/icons-vue";
import { changeChannelApi } from "@/api/cert/certBatchApi";

const props = defineProps<{
  modelValue: boolean;
  endpointId: string;
  currentChannel: number;
  deviceId: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  success: [];
}>();

const visible = ref(props.modelValue);
const selectedChannel = ref(1);
const submitting = ref(false);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      selectedChannel.value = props.currentChannel || 1;
    }
  }
);
watch(visible, (val) => emit("update:modelValue", val));

const handleClose = () => {
  selectedChannel.value = 1;
};

const handleSubmit = async () => {
  if (selectedChannel.value === props.currentChannel) {
    ElMessage.warning("新频道与当前频道相同");
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确认将终端 ${props.deviceId} 从频道 ${props.currentChannel} 切换到频道 ${selectedChannel.value}？`,
      "确认切换频道",
      {
        type: "warning",
        confirmButtonText: "确认切换",
        cancelButtonText: "取消",
      }
    );
  } catch {
    return;
  }

  submitting.value = true;
  try {
    await changeChannelApi(props.endpointId, selectedChannel.value);
    ElMessage.success(`已切换到频道 ${selectedChannel.value}`);
    emit("success");
    visible.value = false;
  } catch (e: any) {
    ElMessage.error(e.message || "切换失败");
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
:deep(.channel-dialog .el-dialog__header) {
  padding: 20px 24px 0;
}
:deep(.channel-dialog .el-dialog__body) {
  padding: 16px 24px;
}
:deep(.channel-dialog .el-dialog__footer) {
  padding: 12px 24px 20px;
  border-top: 1px solid #f1f5f9;
}

/* 顶部英雄区 */
.dialog-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  border-radius: 12px;
  margin-bottom: 20px;
}
.hero-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}
.hero-text h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* 当前信息卡片 */
.current-info {
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
  margin-bottom: 16px;
}
.info-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}
.info-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  font-family: "SF Mono", Monaco, monospace;
}
.current-channel-badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: #ede9fe;
  color: #6d28d9;
}

/* 频道选择 */
.channel-section {
  margin-bottom: 16px;
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 10px;
}
.channel-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.channel-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid #e8ecf1;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}
.channel-option:hover {
  border-color: #c4b5fd;
  background: #faf5ff;
}
.channel-option.active {
  border-color: #7c3aed;
  background: #f5f3ff;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.15);
}
.channel-option.current {
  opacity: 0.6;
}
.channel-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.2s;
}
.channel-option.active .channel-radio {
  border-color: #7c3aed;
}
.radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #7c3aed;
}
.channel-info {
  flex: 1;
}
.channel-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.channel-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}
.current-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
}

/* 警告 */
.warn-alert {
  margin-top: 16px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}
:deep(.warn-alert .el-alert__title) {
  color: #92400e;
}
.warn-text {
  font-size: 13px;
  line-height: 1.8;
  color: #92400e;
}

/* 提交按钮 */
.submit-btn {
  border-radius: 10px !important;
  padding: 0 28px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #7c3aed, #6366f1) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3) !important;
}
.submit-btn:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(124, 58, 237, 0.4) !important;
}
.submit-btn:disabled {
  opacity: 0.5;
}
</style>
